import { useState, useEffect, useCallback, useRef } from 'react';
import { getMessaging, getToken, onMessage, isSupported as checkMessagingSupport } from 'firebase/messaging';
import { app } from '../firebase';
import axios from 'axios';
import { API_URL } from '../config';
import { resolvePushVapidKey } from '../pushNotificationConfig';
import { getDeviceFingerprint, getDeviceType, getBrowserName, getOS } from '../utils/deviceFingerprint';

/**
 * Custom hook for Firebase Cloud Messaging push notifications
 * Handles:
 * - Requesting notification permission
 * - Registering device token with backend
 * - Receiving foreground notifications
 * - Tracking notification clicks
 */

const usePushNotifications = (user = null) => {
  // Safely check if Notification API is available
  const getInitialPermission = () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'default';
  };

  const [permission, setPermission] = useState(getInitialPermission());
  const [deviceToken, setDeviceToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [foregroundMessage, setForegroundMessage] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const registrationPromise = useRef(null);
  const permissionPromise = useRef(null);
  const autoRegistrationAttempted = useRef(false);
  const registeredUser = useRef(null);

  // Check if FCM is supported
  useEffect(() => {
    const checkSupport = async () => {
      // Check if Notification API is available first
      if (typeof window === 'undefined' || !('Notification' in window)) {
        setIsSupported(false);
        return;
      }

      let supported = false;
      try {
        supported = await checkMessagingSupport();
      } catch {
        setError('Push notifications are not supported in this browser');
      }
      setIsSupported(supported);

      // Update permission state if Notification API is available
      if (supported) {
        setPermission(Notification.permission);
      }
    };
    checkSupport();
  }, []);

  /**
   * Register device token with backend. All concurrent callers share the outcome.
   */
  const registerDeviceToken = useCallback(() => {
    if (registrationPromise.current) return registrationPromise.current;
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return Promise.resolve(false);
    }

    autoRegistrationAttempted.current = true;
    setIsLoading(true);
    setError(null);
    const pending = Promise.resolve().then(async () => {
      let failureMessage = 'Push notification configuration is invalid. Please try again later.';
      try {
        const vapidKey = resolvePushVapidKey();
        failureMessage = 'Unable to prepare push notifications. Please try again.';
        const messaging = getMessaging(app);
        const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/'
        });
        failureMessage = 'Unable to obtain a push notification token. Please try again.';
        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: swRegistration
        });
        if (typeof token !== 'string' || !token.trim()) throw new Error();

        // Get current session ID from sessionStorage to link previous browsing activity
        const sessionId = sessionStorage.getItem('eladore_session_id');

        // Register token with backend
        failureMessage = 'Unable to enable push notifications. Please try again.';
        const response = await axios.post(`${API_URL}/push-notifications/register`, {
          deviceToken: token,
          deviceFingerprint: getDeviceFingerprint(),
          deviceType: getDeviceType(),
          browser: getBrowserName(),
          os: getOS(),
          sessionId: sessionId // Include session ID to link past browsing activity
        });
        if (response?.data?.success !== true) throw new Error();
        registeredUser.current = user;
        setDeviceToken(token);
        return true;
      } catch (err) {
        if (err?.code === 'messaging/unsupported-browser') {
          failureMessage = 'Push notifications are not supported in this browser';
        } else if (err?.code === 'messaging/permission-blocked') {
          failureMessage = 'Notification permission was blocked. Please enable it in browser settings.';
        }
        // Never log provider/Axios objects: they can contain tokens and session data.
        console.error('[usePushNotifications] Enrollment failed:', failureMessage);
        setDeviceToken(null);
        setError(failureMessage);
        return false;
      }
    }).finally(() => {
      registrationPromise.current = null;
      setIsLoading(false);
    });
    registrationPromise.current = pending;
    return pending;
  }, [isSupported, user]);

  /**
   * Request permission and report success only after enrollment is acknowledged.
   */
  const requestPermission = useCallback(() => {
    if (permissionPromise.current) return permissionPromise.current;
    if (typeof window === 'undefined' || !('Notification' in window) || !isSupported) {
      setError('Push notifications are not supported in this browser');
      return Promise.resolve(false);
    }

    setIsLoading(true);
    setError(null);
    const pending = Promise.resolve().then(async () => {
      try {
        let result = Notification.permission;
        if (result === 'denied') {
          setError('Notification permission was previously denied. Please enable it in your browser settings.');
          return false;
        }
        if (result !== 'granted') result = await Notification.requestPermission();
        setPermission(result);
        if (result !== 'granted') {
          setError(result === 'denied' ? 'Notification permission was denied' : 'Notification permission was not granted');
          return false;
        }
        const enrolled = await registerDeviceToken();
        if (enrolled) localStorage.removeItem('notifications_disabled');
        return enrolled;
      } catch {
        setError('Unable to request notification permission. Please try again.');
        return false;
      }
    }).finally(() => {
      permissionPromise.current = null;
      setIsLoading(false);
    });
    permissionPromise.current = pending;
    return pending;
  }, [isSupported, registerDeviceToken]);

  /**
   * Unregister device token
   */
  const unregisterDevice = useCallback(async () => {
    if (!deviceToken) {
      return;
    }

    try {
      setIsLoading(true);

      await axios.post(`${API_URL}/push-notifications/unregister`, {
        deviceToken
      });

      setDeviceToken(null);
      // Mark as explicitly disabled by user
      localStorage.setItem('notifications_disabled', 'true');
      console.log('Device unregistered successfully');
    } catch (err) {
      console.error('Error unregistering device:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [deviceToken]);

  /**
   * Track notification click
   */
  const trackClick = useCallback(async (notificationId) => {
    if (!deviceToken || !notificationId) {
      return;
    }

    try {
      // Store notification click for conversion tracking (30 min expiry)
      localStorage.setItem('last_notification_click', JSON.stringify({
        notificationId,
        timestamp: Date.now(),
        expiresAt: Date.now() + (30 * 60 * 1000) // 30 minutes
      }));

      await axios.post(`${API_URL}/push-notifications/track-click`, {
        notificationId,
        deviceToken
      });
    } catch (err) {
      console.error('Error tracking notification click:', err);
    }
  }, [deviceToken]);

  /**
   * Listen for service worker messages (notification clicks/dismissals)
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const handleMessage = (event) => {
      // Handle both Firebase's built-in format and our custom format
      let type, notificationId;

      // Check if this is Firebase's built-in notification-clicked event
      if (event.data?.messageType === 'notification-clicked' || event.data?.messageType === 'push-received') {
        type = event.data.messageType === 'notification-clicked' ? 'NOTIFICATION_CLICKED' : 'PUSH_RECEIVED';
        notificationId = event.data?.data?.notification_id; // Nested in data
      } else {
        // Our custom service worker postMessage format
        ({ type, notificationId } = event.data || {});
      }

      if (type === 'NOTIFICATION_CLICKED') {
        if (notificationId) {
          trackClick(notificationId);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [trackClick]);

  /**
   * Listen for foreground messages
   */
  useEffect(() => {
    if (!isSupported || permission !== 'granted') {
      return;
    }

    const messaging = getMessaging(app);

    const unsubscribe = onMessage(messaging, (payload) => {
      setForegroundMessage({
        title: payload.notification?.title,
        body: payload.notification?.body,
        icon: payload.notification?.icon,
        image: payload.notification?.image,
        clickAction: payload.data?.click_action || '/',
        notificationId: payload.data?.notification_id
      });

      // Auto-clear after 5 seconds
      setTimeout(() => {
        setForegroundMessage(null);
      }, 5000);
    });

    return () => {
      unsubscribe();
    };
  }, [isSupported, permission]);

  /**
   * Auto-register on mount if permission already granted
   * BUT respect user's explicit disable choice
   */
  useEffect(() => {
    const isDisabled = localStorage.getItem('notifications_disabled') === 'true';

    if (permission === 'granted' && !deviceToken && isSupported && !isDisabled && !autoRegistrationAttempted.current) {
      registerDeviceToken();
    }
  }, [permission, deviceToken, registerDeviceToken, isSupported]);

  /**
   * Re-register when user logs in
   */
  useEffect(() => {
    if (user && deviceToken && registeredUser.current !== user) {
      // Update the token registration with user ID
      registerDeviceToken();
    }
  }, [user, deviceToken, registerDeviceToken]);

  return {
    permission,
    deviceToken,
    isLoading,
    error,
    foregroundMessage,
    isSupported,
    requestPermission,
    unregisterDevice,
    trackClick,
    clearForegroundMessage: () => setForegroundMessage(null)
  };
};

export default usePushNotifications;
